import React from 'react';
import {
  get,
  find,
  forEach,
  isNaN,
  minBy,
  reduce,
} from 'lodash';
import VibeIcon from '../components/VibeIcon/VibeIcon';
import viCompany from '../icons/viCompany';
import viLocationOn from '../icons/viLocationOn';
import * as color from '../sass/color.scss';

const COMMON_ASPECT_RATIOS = [
  ['1:1', 1],
  ['2:1', 2],
  ['4:3', 1.3333333333333333],
  ['3:2', 1.5],
  ['14:9', 1.5555555555555556],
  ['16:10', 1.6],
  ['16:9', 1.7777777777777777],
  ['1.85:1', 1.85],
  ['21:9', 2.3333333333333335],
  ['2.4:1', 2.4],
  ['32:9', 3.5555555555555554],
  ['4:1', 4],
];

/**
 * Get the display for the location count based on the number of companies/locations assigned to the object
 */
function getLocationCountDisplay(numCompanies, numLocations, isACAF) {
  const itemDisplayStyle = {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 8,
    padding: 8,
    backgroundColor: color.manatee16,
    borderRadius: 4,
  };

  // only needed for objects not assigned to all current and future
  const element = !isACAF
    ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 24,
        }}
      >
        {numCompanies > 0 && (
          <div
            style={itemDisplayStyle}
          >
            <VibeIcon
              icon={viCompany}
              color={color.manatee}
              size={16}
              style={{
                marginRight: 4,
              }}
            />

            {numCompanies}
          </div>
        )}

        {numLocations > 0 && (
          <div
            style={itemDisplayStyle}
          >
            <VibeIcon
              icon={viLocationOn}
              color={color.manatee}
              size={16}
              style={{
                marginRight: 4,
              }}
            />

            {numLocations}
          </div>
        )}

        {numCompanies <= 0 && numLocations <= 0 && (
          <div
            style={itemDisplayStyle}
          >
            0
          </div>
        )}
      </div>
    )
    : 'All Current and Future';

  // only needed for objects not assigned to all current and future
  const tooltip = !isACAF
    ? (
      <div
        style={{
          lineHeight: 1.4,
        }}
      >
        {numCompanies > 0 && (
          <div>
            {numCompanies} {numCompanies === 1 ? 'Company' : 'Companies'} Assigned
          </div>
        )}

        {numLocations > 0 && (
          <div>
            {numLocations} {numLocations === 1 ? 'Location' : 'Locations'} Assigned
          </div>
        )}

        {numCompanies <= 0 && numLocations <= 0 && (
          <div>
            No Locations Assigned
          </div>
        )}
      </div>
    )
    : '';

  return {
    element,
    tooltip,
  };
}

/**
 * Get the total location count for the object
 */
export function getLocationCount(companies, isACAF) {
  // get companies assigned to ACAF
  const companiesACAF = companies.filter(company => company.locationCount === '*');
  // get companies with a specific location count
  const companiesWithLocations = companies.filter(company => get(company, 'locations', []).length > 0);

  const numCompanies = companiesACAF.length;
  const numLocations = reduce(companiesWithLocations, (total, company) => {
    return total + get(company, 'locations', []).length;
  }, 0);

  const display = getLocationCountDisplay(numCompanies, numLocations, isACAF);

  return {
    // if ACAF is selected, count as a location
    total: numCompanies + numLocations + (isACAF ? 1 : 0),
    numCompanies,
    numLocations,
    display,
  };
}

/**
 * Compare two sets of location specs
 */
export function compare(parentLocations, childLocations) {
  const response = {
    parent: {
      missing: [],
    },
    child: {
      missing: [],
    },
  };

  const parentAllLocations = find(parentLocations, { companyId: '*' }) !== undefined;
  const childAllLocations = find(childLocations, { companyId: '*' }) !== undefined;

  if (parentAllLocations || childAllLocations) {
    // Parent has all locations, all child locations are included
    return response;
  }

  parentLocations.forEach((location) => {
    const {
      companyId,
      locationId,
    } = location;

    // Parent location is included in the child location spec
    const childHasLocation = find(childLocations, { companyId, locationId }) !== undefined;

    if (!childHasLocation) {
      // Parent location is not included in the child location spec
      // Check if the child has access to all locations for this company
      const childHasAllLocationsForCompany = find(childLocations, { companyId, locationId: '*' }) !== undefined;

      if (!childHasAllLocationsForCompany) {
        // Child does not have access to all locations for the parent location company
        response.parent.missing.push(location);
      }
    }
  });

  childLocations.forEach((location) => {
    const {
      companyId,
      locationId,
    } = location;

    // Child location is included in the parent location spec
    const parentHasLocation = find(parentLocations, { companyId, locationId }) !== undefined;

    if (!parentHasLocation) {
      // Parent does not have this specific location
      // Check if the parent has access to all locations for that company
      const parentHasAllLocationsForCompany = find(parentLocations, { companyId, locationId: '*' }) !== undefined;

      if (!parentHasAllLocationsForCompany) {
        // Child location is not included in the parent location spec
        // Check if the child location is all locations for the company
        // and the parent location has at least one location for the company
        const childHasAllLocationsForCompany = locationId === '*' && find(parentLocations, { companyId }) !== undefined;

        if (!childHasAllLocationsForCompany) {
          // Child location does not have all locations for the company
          // child not included in the location spec
          response.child.missing.push(location);
        }
      }
    }
  });

  return response;
}

/**
 * Determine if a message is applicable to play at a given location
 *
 * A message is considered applicable when:
 * - It is directly assigned to the location, OR
 * - It is assigned to "all current and future" (companyId === '*' or
 *   locationId === '*' for the matching company)
 *
 * When neither companyId nor locationId is specified (e.g., the card is being
 * shown outside of a location context) the message is compared against the
 * parent locations to determine applicability.
 */
export function isMessageApplicable({
  companyId,
  locationId,
  locations = [],
  parentLocations = [],
} = {}) {
  if ((companyId && companyId !== '*') || (locationId && locationId !== '*')) {
    return locations.some((loc) => (
      loc.locationId === locationId
      || loc.companyId === '*'
      || (loc.locationId === '*' && (loc.companyId === companyId || companyId === '*'))
    ));
  }

  if (companyId === '*' || locationId === '*') {
    return true;
  }

  const compareResult = compare(parentLocations, locations);
  return compareResult.parent.missing.length <= 0;
}

/**
 * Group a location spec into an object of company IDs with an array of location IDs
 */
export function group(locations = []) {
  const group = {};

  locations.forEach((location) => {
    const {
      companyId,
      locationId,
    } = location;

    if (!group[companyId]) {
      group[companyId] = [];
    }

    if (locationId) {
      group[companyId].push(locationId);
    }
  });

  return group;
}

/**
 * Convert a group of locations to an array used for all our location specs
 */
export function toSpec(group = {}) {
  const spec = [];

  forEach(group, (locations, companyId) => {
    forEach(locations, (locationId) => {
      spec.push({
        companyId,
        locationId,
      });
    });
  });

  return spec;
}

/**
 * Check to see if two sets of locations has any matches that overlap
 */
export function overlap(firstLocations, secondLocations) {
  let hasOverlap = false;

  const firstAllLocations = find(firstLocations, { companyId: '*' }) !== undefined;
  const secondAllLocations = find(secondLocations, { companyId: '*' }) !== undefined;

  if (firstAllLocations || secondAllLocations) {
    hasOverlap = true;
    return hasOverlap;
  }

  for (let i = 0; i < firstLocations.length; i++) {
    const {
      companyId,
      locationId,
    } = firstLocations[i];

    const secondHasLocation = locationId === '*'
      // location has access to the whole company, find any location with that company in the second locations
      ? find(secondLocations, { companyId }) !== undefined
      // does any second location set have the first location?
      : find(secondLocations, { companyId, locationId }) !== undefined;

    if (secondHasLocation) {
      hasOverlap = true;
      break;
    }

    // Check if the child has access to all locations for this company
    const secondHasAllLocationsForCompany = find(secondLocations, { companyId, locationId: '*' }) !== undefined;

    if (secondHasAllLocationsForCompany) {
      // Second locations set has access to all locations for the first location company
      hasOverlap = true;
      break;
    }
  }

  return hasOverlap;
}

const cleanDimensionValue = value => {
  const n = Number(value);

  return isNaN(n) || n < 0
    ? 0
    : n;
};

export const getDisplayAspectRatio = (screenDimensions, pixelDimensions) => {
  const [w, h] = screenDimensions.map(cleanDimensionValue);

  if (w === 0 || h === 0) {
    return 'Unknown';
  }

  // Storage Aspect Ratio (SAR):
  //    The ratio of horizontal to vertical pixels in each direction
  const sar = w / h;

  // Pixel Aspect Ratio (PAR):
  //    The ratio of each pixel which may or may not be square
  const par = pixelDimensions
    ? (([pw, ph]) => pw / ph)(pixelDimensions.map(cleanDimensionValue))
    : 1.0; // Assume square pixels if pixelDimensions aren't provided

  // Display Aspect Ratio (DAR):
  //    The aspect ratio perceived by the viewer based on multiplying
  //    the SAR and PAR values
  const dar = sar * par;

  const [aspectRatioName] = minBy(
    COMMON_ASPECT_RATIOS,
    ([, car]) => Math.abs(dar - car));

  return aspectRatioName || 'Unknown';
};

export default {
  getLocationCount,
  compare,
  isMessageApplicable,
  group,
  toSpec,
  overlap,
  getDisplayAspectRatio,
};
